import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { TrendingUp, Clock, Video, FileText, Calendar, UserCog, Check, X, Copy, Users, Crown, Database, Settings, ArrowLeft, ChevronRight, Share2, Activity, Play, Zap } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import TeamGate from '../components/TeamGate';
import { useToast } from '../hooks/use-toast';
import { cn } from '../lib/utils';

import { useAuth } from '../context/AuthContext';
import { useTeam } from '../context/TeamContext';
import { analyticsAPI, teamsAPI, calendarAPI } from '../services/api';
import NextTrainingWidget from '../components/NextTrainingWidget';
import RecurringEventModal from '../components/RecurringEventModal';
import PremiumPromotionModal from '../components/PremiumPromotionModal';
import { useNavigate } from 'react-router-dom';


const Analytics = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, updateProfile } = useAuth();
  const { refreshTeamStatus } = useTeam();
  const { toast } = useToast();
  const isCoach = user?.role === 'coach' || user?.role === 'admin';

  const [timeWindow, setTimeWindow] = useState('30');
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showManageTeam, setShowManageTeam] = useState(false);
  const [showTeamSettings, setShowTeamSettings] = useState(false);
  const [teamSettingsData, setTeamSettingsData] = useState({ name: '', autoCreateSessions: false });
  const [logoFile, setLogoFile] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [requests, setRequests] = useState([]);
  const [teamInfo, setTeamInfo] = useState(null);
  const [copiedCode, setCopiedCode] = useState(false);
  const [refreshingCode, setRefreshingCode] = useState(false);
  const [showRecurringModal, setShowRecurringModal] = useState(false);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [metricsLimit, setMetricsLimit] = useState(5);

  React.useEffect(() => {
    fetchAnalytics();
    if (user?.team_id) {
      loadTeamInfo();
    }
    if (isCoach && user?.team_id) {
      loadJoinRequests();
    }
  }, [timeWindow, user?.team_id, isCoach]);

  const fetchAnalytics = async () => {
    if (!user?.team_id) return;

    setLoading(true);
    try {
      const response = await analyticsAPI.getEngagement(user.team_id, parseInt(timeWindow));
      setMetrics(response.data.metrics);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadTeamInfo = async () => {
    if (!user?.team_id) return;
    try {
      const response = await teamsAPI.get(user.team_id);
      setTeamInfo(response.data);
      setTeamSettingsData({ name: response.data.name || '' });
    } catch (error) {
      console.error('Failed to load team info:', error);
    }
  };

  const handleCopyCode = () => {
    if (teamInfo?.player_join_code) {
      navigator.clipboard.writeText(teamInfo.player_join_code);
      setCopiedCode(true);
      setTimeout(() => setCopiedCode(false), 2000);
    }
  };

  const handleRefreshCode = async () => {
    if (!user?.team_id) return;

    if (!window.confirm('Are you sure you want to regenerate the team Join Code? The old code will stop working.')) {
      return;
    }

    setRefreshingCode(true);
    try {
      await teamsAPI.regenerateCode(user.team_id);
      await loadTeamInfo();
      toast({ title: 'Team code regenerated successfully' });
    } catch (error) {
      console.error('Failed to regenerate code:', error);
      toast({ title: 'Failed to regenerate code', variant: 'destructive' });
    } finally {
      setRefreshingCode(false);
    }
  };

  const loadJoinRequests = async () => {
    if (!user?.team_id) return;
    try {
      const response = await teamsAPI.listJoinRequests('pending');
      setRequests(response.data);
    } catch (error) {
      console.error('Failed to load join requests:', error);
    }
  };

  const handleApprove = async (requestId) => {
    try {
      await teamsAPI.approveJoinRequest(requestId);
      setRequests(requests.map(r => r.id === requestId ? { ...r, status: 'approved' } : r));
      toast({ title: 'Player approved and added to team' });
    } catch (error) {
      console.error('Failed to approve:', error);
      toast({ title: 'Failed to approve request', variant: 'destructive' });
    }
  };

  const handleDeny = async (requestId) => {
    try {
      await teamsAPI.rejectJoinRequest(requestId);
      setRequests(requests.map(r => r.id === requestId ? { ...r, status: 'rejected' } : r));
      toast({ title: 'Join request denied' });
    } catch (error) {
      console.error('Failed to deny:', error);
      toast({ title: 'Failed to deny request', variant: 'destructive' });
    }
  };

  const [sortField, setSortField] = useState('total_active_minutes');
  const [sortDirection, setSortDirection] = useState('desc');

  const sortedMetrics = [...metrics].sort((a, b) => {
    const aVal = a[sortField];
    const bVal = b[sortField];
    return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
  });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };



  const formatMinutes = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const handleSubmitRecurring = async (data) => {
    try {
      await calendarAPI.createRecurringEvent(user.team_id, data);
      setShowRecurringModal(false);
      toast({ title: 'Training schedule updated successfully' });
    } catch (error) {
      console.error('Failed to save recurring event:', error);
      toast({ title: 'Failed to update schedule', variant: 'destructive' });
    }
  };

  return (
    <TeamGate title={t('analytics.title')}>
      <div className="min-h-screen bg-[#0a0a0b] text-white pb-24 animate-in fade-in duration-500">
        {/* Mobile Sticky Header */}
        <div className="lg:hidden p-4 bg-[#0a0a0b]/80 backdrop-blur-xl sticky top-0 z-[100] border-b border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-white/10 rounded-full transition-colors active:scale-90"
            >
              <ArrowLeft className="w-6 h-6 text-snapp-blue" />
            </button>
            <h2 className="text-xl font-black text-white tracking-tight uppercase">
              {t('analytics.title')}
            </h2>
          </div>
          <div className="w-10 h-10 bg-snapp-blue/10 rounded-full flex items-center justify-center">
            <Activity className="w-5 h-5 text-snapp-blue" />
          </div>
        </div>

        <div className="p-4 lg:p-8 space-y-8">
          {/* Desktop Header */}
          <div className="hidden lg:flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-black text-white uppercase tracking-tight">{t('analytics.title')}</h1>
              <p className="text-gray-400 font-medium mt-1">{t('analytics.subtitle')}</p>
            </div>
            <div className="flex flex-wrap gap-3">
              {isCoach && (
                <>
                  <button
                    onClick={() => setShowRecurringModal(true)}
                    className="bg-snapp-blue hover:bg-snapp-blue/90 text-white px-6 py-2.5 rounded-2xl font-black text-xs uppercase tracking-wider transition-all active:scale-95 shadow-lg shadow-blue-500/20 flex items-center gap-2"
                  >
                    <Calendar className="w-4 h-4" /> {t('analytics.manageTrainings')}
                  </button>
                  <button
                    onClick={() => setShowManageTeam(true)}
                    className="bg-snapp-purple hover:bg-snapp-purple/90 text-white px-6 py-2.5 rounded-2xl font-black text-xs uppercase tracking-wider transition-all active:scale-95 shadow-lg shadow-purple-500/20 flex items-center gap-2"
                  >
                    <UserCog className="w-4 h-4" /> {t('analytics.manageTeam')} {requests.length > 0 && `(${requests.length})`}
                  </button>
                  <button
                    onClick={() => {
                      setTeamSettingsData({
                        name: teamInfo?.name || user?.team_name || '',
                        autoCreateSessions: teamInfo?.auto_create_sessions !== undefined ? teamInfo.auto_create_sessions : true
                      });
                      setShowTeamSettings(true);
                    }}
                    className="p-2.5 bg-[#1a1a1b] border border-white/5 rounded-2xl text-white hover:bg-white/5 transition-all active:scale-95 shadow-xl"
                  >
                    <Settings className="w-5 h-5" />
                  </button>
                </>
              )}
              <Select value={timeWindow} onValueChange={setTimeWindow}>
                <SelectTrigger className="w-[180px] bg-[#1a1a1b] border-white/5 text-white rounded-2xl h-11 font-black text-xs uppercase tracking-widest">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1a1a1b] border-white/5 text-white">
                  <SelectItem value="7">{t('analytics.last7Days')}</SelectItem>
                  <SelectItem value="30">{t('analytics.last30Days')}</SelectItem>
                  <SelectItem value="90">{t('analytics.last90Days')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Time Window Selector for Mobile */}
          <div className="lg:hidden bg-[#1a1a1b]/60 backdrop-blur-xl border border-white/5 p-1.5 rounded-[1.5rem] flex gap-2">
            {['7', '30', '90'].map((window) => (
              <button
                key={window}
                onClick={() => setTimeWindow(window)}
                className={cn(
                  "flex-1 py-3.5 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all duration-300",
                  timeWindow === window ? "bg-snapp-blue text-white shadow-xl shadow-blue-500/20" : "text-gray-500 hover:text-white hover:bg-white/5"
                )}
              >
                {window} Days
              </button>
            ))}
          </div>

          {teamInfo && (
            <div className={cn(
              "relative bg-[#1a1a1b]/60 backdrop-blur-xl border rounded-[2rem] p-6 shadow-2xl overflow-hidden group transition-all duration-500",
              teamInfo.plan === 'premium' ? "border-amber-500/20 bg-gradient-to-br from-amber-500/5 to-transparent" : "border-white/5"
            )}>
              {/* Background Glow */}
              <div className={cn(
                "absolute -top-24 -right-24 w-64 h-64 blur-[100px] opacity-20 pointer-events-none transition-all duration-1000 group-hover:opacity-30",
                teamInfo.plan === 'premium' ? "bg-amber-500" : "bg-snapp-blue"
              )} />

              <div className="relative space-y-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={cn(
                      "p-3 rounded-2xl transition-transform duration-500 group-hover:scale-110 shadow-xl",
                      teamInfo.plan === 'premium' ? "bg-amber-500 shadow-amber-500/20" : "bg-snapp-blue shadow-blue-500/20"
                    )}>
                      {teamInfo.plan === 'premium' ? <Crown className="w-6 h-6 text-white fill-current" /> : <Database className="w-6 h-6 text-white" />}
                    </div>
                    <div>
                      <h3 className="text-xl font-black text-white uppercase tracking-tight">{t('analytics.storageUsage')}</h3>
                      <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mt-0.5">Cloud Performance</p>
                    </div>
                  </div>
                  {teamInfo.plan === 'premium' ? (
                    <div className="px-4 py-1.5 bg-amber-500 rounded-full shadow-lg shadow-amber-500/30">
                      <span className="text-[10px] font-black text-[#0a0a0b] uppercase tracking-widest">PREMIUM ELITE</span>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowPremiumModal(true)}
                      className="px-6 py-2.5 bg-amber-500 hover:bg-amber-400 text-[#0a0a0b] rounded-2xl font-black text-xs uppercase tracking-wider transition-all active:scale-95 shadow-xl shadow-amber-500/20 flex items-center gap-2"
                    >
                      <Zap className="w-4 h-4 fill-current" /> {t('analytics.upgradeToPremium')}
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-[1.2fr,1fr] gap-6 items-center">
                  <div className="space-y-4">
                    <div className="flex items-baseline gap-3">
                      <p className="text-4xl font-black text-white tracking-tighter">
                        {(teamInfo.storage_used_bytes / (1024 * 1024 * 1024)).toFixed(2)}
                      </p>
                      <p className="text-xs font-black text-gray-600 uppercase tracking-widest">
                        GB / {teamInfo.storage_limit_gb} GB Max
                      </p>
                    </div>

                    <div className="space-y-2">
                      {/* Premium Progress Bar */}
                      <div className="h-4 w-full bg-[#0a0a0b] rounded-full overflow-hidden p-1 border border-white/5 relative shadow-inner">
                        <div
                          className={cn(
                            "h-full rounded-full transition-all duration-1000 shadow-lg relative",
                            ((teamInfo.storage_used_bytes / (1024 * 1024 * 1024)) / teamInfo.storage_limit_gb) * 100 > 90
                              ? "bg-gradient-to-r from-red-600 to-red-400 shadow-red-500/20"
                              : teamInfo.plan === 'premium' ? "bg-gradient-to-r from-amber-600 to-amber-400 shadow-amber-500/20" : "bg-gradient-to-r from-snapp-blue to-snapp-purple shadow-blue-500/20"
                          )}
                          style={{ width: `${Math.min(((teamInfo.storage_used_bytes / (1024 * 1024 * 1024)) / teamInfo.storage_limit_gb) * 100, 100)}%` }}
                        >
                          {/* Animated Shine */}
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent w-full animate-[shimmer_2s_infinite]" />
                        </div>
                      </div>
                      <div className="flex justify-between items-center px-1">
                        <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest">System Efficiency</p>
                        <p className={cn(
                          "text-[10px] font-black uppercase tracking-widest",
                          ((teamInfo.storage_used_bytes / (1024 * 1024 * 1024)) / teamInfo.storage_limit_gb) * 100 > 90 ? "text-red-500" : "text-snapp-blue"
                        )}>
                          {Math.round(((teamInfo.storage_used_bytes / (1024 * 1024 * 1024)) / teamInfo.storage_limit_gb) * 100)}% Occupied
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-5 bg-white/5 rounded-2xl border border-white/5 backdrop-blur-md">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Storage Insights</p>
                    <p className="text-xs font-medium text-gray-300 leading-relaxed">
                      {teamInfo.plan === 'premium'
                        ? "Elite storage plan active. You have full capacity for high-resolution 4K performance clips."
                        : "Current storage allows for basic performance clips. Upgrade to Elite for 100GB of space."
                      }
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <NextTrainingWidget />

          {/* Team Code Card - Premium Mobile */}
          {teamInfo && (
            <div className="bg-[#1a1a1b]/60 backdrop-blur-xl border border-white/5 rounded-[2rem] p-8 shadow-xl">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                <div className="space-y-4 flex-1">
                  <div>
                    <h3 className="text-lg font-black text-white uppercase tracking-tight">{t('analytics.teamJoinCode')}</h3>
                    <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mt-0.5">Invitation Alpha-Key</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="bg-[#0a0a0b] px-6 py-4 rounded-2xl border border-snapp-purple/20 shadow-inner group">
                      <code className="text-3xl font-black text-white tracking-[0.2em] group-active:text-snapp-purple transition-colors">
                        {teamInfo.player_join_code || 'N/A'}
                      </code>
                    </div>
                    <button
                      onClick={handleCopyCode}
                      className="p-4 bg-white/5 hover:bg-snapp-purple/10 text-gray-400 hover:text-snapp-purple rounded-2xl transition-all active:scale-90"
                    >
                      {copiedCode ? <Check className="w-6 h-6" /> : <Copy className="w-6 h-6" />}
                    </button>
                    {isCoach && (
                      <button
                        onClick={handleRefreshCode}
                        disabled={refreshingCode}
                        className={cn(
                          "p-4 bg-white/5 hover:bg-red-500/10 text-gray-400 hover:text-red-500 rounded-2xl transition-all active:scale-90",
                          refreshingCode && "animate-spin"
                        )}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" /><path d="M8 16H3v5" /></svg>
                      </button>
                    )}
                  </div>
                  <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest">{t('analytics.shareCodeParams')}</p>
                </div>
                <div className="hidden sm:block p-8 bg-snapp-purple/10 rounded-[2.5rem] border border-snapp-purple/20">
                  <Users className="w-16 h-16 text-snapp-purple" />
                </div>
              </div>
            </div>
          )}

          {/* Engagement Metrics - Premium Grid for Mobile */}
          <div className="space-y-6">
            <div className="flex items-center justify-between px-2">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-snapp-blue/10 rounded-xl">
                  <TrendingUp className="w-5 h-5 text-snapp-blue" />
                </div>
                <div>
                  <h3 className="text-xl font-black text-white uppercase tracking-tight">{t('analytics.engagementMetrics')}</h3>
                  <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mt-0.5">Player Performance</p>
                </div>
              </div>
              {sortedMetrics.length > 5 && (
                <button
                  onClick={() => setMetricsLimit(prev => prev === 5 ? sortedMetrics.length : 5)}
                  className="px-4 py-2 bg-white/5 hover:bg-white/10 rounded-xl font-black text-[10px] uppercase tracking-widest text-snapp-blue transition-all active:scale-95"
                >
                  {metricsLimit === 5 ? t('dashboard.viewAll') : t('analytics.showLess')}
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:hidden gap-4">
              {sortedMetrics.slice(0, metricsLimit).map((metric) => (
                <div key={metric.user_id} className="bg-[#1a1a1b]/60 backdrop-blur-xl border border-white/5 rounded-[2rem] p-6 shadow-xl space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-white font-black uppercase tracking-tight">{metric.username}</h4>
                    <div className="px-3 py-1 bg-snapp-blue/10 rounded-full">
                      <span className="text-[10px] font-black text-snapp-blue tracking-widest">RANK TOP</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-[9px] font-black text-gray-600 uppercase tracking-widest">{t('analytics.activeTime')}</p>
                      <p className="text-sm font-black text-white tabular-nums">{formatMinutes(metric.total_active_minutes)}</p>
                    </div>
                    <div className="space-y-1 text-right">
                      <p className="text-[9px] font-black text-gray-600 uppercase tracking-widest">{t('analytics.activeDays')}</p>
                      <p className="text-sm font-black text-white tabular-nums">{metric.active_days} Days</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-[9px] font-black text-gray-600 uppercase tracking-widest">Attendance</p>
                      <p className="text-sm font-black text-snapp-green tabular-nums">%{Math.round(metric.attendance_rate || 0)}</p>
                    </div>
                    <div className="space-y-1 text-right">
                      <p className="text-[9px] font-black text-gray-600 uppercase tracking-widest">Avg Session</p>
                      <p className="text-sm font-black text-white tabular-nums">{metric.avg_session_minutes.toFixed(1)}m</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Metrics Table */}
            <div className="hidden lg:block bg-[#1a1a1b]/60 backdrop-blur-xl border border-white/5 rounded-[2rem] overflow-hidden shadow-xl">
              <Table>
                <TableHeader className="bg-white/5">
                  <TableRow className="border-white/5 hover:bg-transparent">
                    <TableHead className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] py-6 px-8">{t('analytics.player')}</TableHead>
                    <TableHead className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] cursor-pointer hover:text-white" onClick={() => handleSort('total_active_minutes')}>{t('analytics.activeTime')} {sortField === 'total_active_minutes' && (sortDirection === 'asc' ? '↑' : '↓')}</TableHead>
                    <TableHead className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] cursor-pointer hover:text-white" onClick={() => handleSort('active_days')}>{t('analytics.activeDays')} {sortField === 'active_days' && (sortDirection === 'asc' ? '↑' : '↓')}</TableHead>
                    <TableHead className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] cursor-pointer hover:text-white" onClick={() => handleSort('attendance_rate')}>{t('analytics.trainingAttendance')} {sortField === 'attendance_rate' && (sortDirection === 'asc' ? '↑' : '↓')}</TableHead>
                    <TableHead className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] cursor-pointer hover:text-white" onClick={() => handleSort('avg_session_minutes')}>{t('analytics.avgSession')} {sortField === 'avg_session_minutes' && (sortDirection === 'asc' ? '↑' : '↓')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedMetrics.slice(0, metricsLimit).map((metric) => (
                    <TableRow key={metric.user_id} className="border-white/5 hover:bg-white/5 transition-colors">
                      <TableCell className="text-white font-black uppercase tracking-tight py-6 px-8">{metric.username}</TableCell>
                      <TableCell className="text-gray-300 font-bold tabular-nums">{formatMinutes(metric.total_active_minutes)}</TableCell>
                      <TableCell className="text-gray-300 font-bold tabular-nums">{metric.active_days}</TableCell>
                      <TableCell className="text-snapp-green font-black tabular-nums">%{Math.round(metric.attendance_rate || 0)}</TableCell>
                      <TableCell className="text-gray-300 font-bold tabular-nums">{metric.avg_session_minutes.toFixed(1)} min</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>

        {/* Coach Floating Action Buttons for Mobile */}
        {isCoach && (
          <div className="lg:hidden fixed bottom-28 right-6 flex flex-col gap-3 z-[101]">
            <button
              onClick={() => setShowRecurringModal(true)}
              className="w-14 h-14 bg-snapp-blue text-white rounded-2xl shadow-2xl flex items-center justify-center transition-all active:scale-90"
            >
              <Calendar className="w-7 h-7" />
            </button>
            <button
              onClick={() => setShowManageTeam(true)}
              className="w-14 h-14 bg-snapp-purple text-white rounded-2xl shadow-2xl flex items-center justify-center transition-all active:scale-90"
            >
              <UserCog className="w-7 h-7" />
              {requests.length > 0 && (
                <span className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 text-white text-[10px] font-black rounded-full flex items-center justify-center border-2 border-[#0a0a0b]">
                  {requests.length}
                </span>
              )}
            </button>
          </div>
        )}

        {/* Team Management Modal */}
        {
          showManageTeam && (
            <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
              <Card className="bg-[#1a1a1b] border-gray-800 w-full max-w-3xl max-h-[80vh] overflow-hidden">
                <CardHeader className="border-b border-gray-800">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white flex items-center gap-2">
                      <UserCog className="w-5 h-5" />
                      {t('analytics.manageTeam')}
                    </CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => setShowManageTeam(false)} className="text-gray-400 hover:text-white">
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-0 overflow-y-auto max-h-[calc(80vh-80px)]">
                  <Tabs defaultValue="requests" className="w-full">
                    <TabsList className="bg-[#0a0a0b] m-4 mb-0">
                      <TabsTrigger value="requests">
                        <Clock className="w-4 h-4 mr-2" />
                        {t('analytics.pendingRequests')} ({requests.length})
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="requests" className="p-4 pt-2">
                      {requests.length === 0 ? (
                        <div className="text-center py-12">
                          <Clock className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                          <p className="text-gray-400">{t('analytics.noPendingRequests')}</p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {requests.map((request) => (
                            <div
                              key={request.id}
                              className="flex items-center justify-between p-4 bg-[#0a0a0b] rounded-lg"
                            >
                              <div>
                                <h3 className="text-white font-medium flex items-center gap-2">
                                  {request.username || 'Unknown User'}
                                  {request.type === 'position_update' && (
                                    <Badge variant="outline" className="text-xs border-blue-500/50 text-blue-400">
                                      Position Change
                                    </Badge>
                                  )}
                                </h3>
                                {request.type === 'position_update' && request.data?.positions ? (
                                  <p className="text-sm text-blue-300 mt-1">
                                    Requested: {request.data.positions.join(', ')}
                                  </p>
                                ) : (
                                  <p className="text-sm text-gray-400">
                                    Requested {request.created_at ? format(parseISO(request.created_at), 'MMM d, yyyy HH:mm') : '-'}
                                  </p>
                                )}
                              </div>
                              <div className="flex gap-2 min-w-[160px] justify-end">
                                {request.status === 'pending' || !request.status ? (
                                  <>
                                    <Button
                                      size="sm"
                                      onClick={() => handleApprove(request.id)}
                                      className="bg-green-600 hover:bg-green-700"
                                    >
                                      <Check className="w-4 h-4 mr-1" />
                                      Onayla
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      onClick={() => handleDeny(request.id)}
                                    >
                                      <X className="w-4 h-4 mr-1" />
                                      Reddet
                                    </Button>
                                  </>
                                ) : request.status === 'approved' ? (
                                  <span className="text-green-500 font-medium flex items-center pr-2">
                                    <Check className="w-4 h-4 mr-1" />
                                    Onaylandı
                                  </span>
                                ) : (
                                  <span className="text-red-500 font-medium flex items-center pr-2">
                                    <X className="w-4 h-4 mr-1" />
                                    Reddedildi
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </div>
          )
        }

        {/* Team Settings Modal */}
        {showTeamSettings && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <Card className="bg-[#1a1a1b] border-gray-800 w-full max-w-md">
              <CardHeader className="border-b border-gray-800">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    {t('settings.teamSettings') || "Takım Ayarları"}
                  </CardTitle>
                  <Button variant="ghost" size="sm" onClick={() => setShowTeamSettings(false)} className="text-gray-400 hover:text-white">
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6">
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  if (!teamSettingsData.name.trim()) return;

                  setSavingSettings(true);
                  try {
                    // Collect update data
                    const updateData = {};
                    if (teamSettingsData.name !== teamInfo?.name) {
                      updateData.name = teamSettingsData.name;
                    }
                    if (teamSettingsData.autoCreateSessions !== teamInfo?.auto_create_sessions) {
                      updateData.auto_create_sessions = teamSettingsData.autoCreateSessions;
                    }

                    // 1. Update team settings if any changed
                    if (Object.keys(updateData).length > 0) {
                      await teamsAPI.update(user.team_id, updateData);
                      if (updateData.name) {
                        updateProfile({ team_name: updateData.name });
                      }
                    }

                    // 2. Upload logo if selected
                    if (logoFile) {
                      await teamsAPI.uploadLogo(user.team_id, logoFile);
                    }

                    toast({ title: t('common.success') || 'Settings saved successfully' });
                    setLogoFile(null); // Reset logo file state
                    await loadTeamInfo(); // reload to get new team name/logo
                    refreshTeamStatus(); // refresh TeamContext state so TeamGate and Sidebar update
                    setShowTeamSettings(false);
                  } catch (error) {
                    console.error('Failed to update team settings:', error);
                    toast({ title: t('common.error') || 'Failed to update settings', variant: 'destructive' });
                  } finally {
                    setSavingSettings(false);
                  }
                }} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="teamName" className="text-white">{t('settings.teamName') || 'Takım Adı'}</Label>
                    <Input
                      id="teamName"
                      value={teamSettingsData.name}
                      onChange={(e) => setTeamSettingsData({ ...teamSettingsData, name: e.target.value })}
                      className="bg-[#0a0a0b] border-gray-700 text-white"
                      placeholder="Takım adı girin"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="teamLogo" className="text-white">Takım Logosu</Label>
                    {teamInfo?.logo_url && !logoFile && (
                      <div className="mb-2">
                        <img src={teamInfo.logo_url} alt="Current Logo" className="w-16 h-16 object-contain rounded bg-white/10" />
                      </div>
                    )}
                    <Input
                      id="teamLogo"
                      type="file"
                      accept="image/png, image/jpeg, image/webp"
                      onChange={(e) => {
                        if (e.target.files && e.target.files.length > 0) {
                          setLogoFile(e.target.files[0]);
                        }
                      }}
                      className="bg-[#0a0a0b] border-gray-700 text-white cursor-pointer"
                    />
                  </div>

                  <div className="flex items-center justify-between py-3 px-1">
                    <div className="space-y-0.5">
                      <Label className="text-white text-sm">Otomatik Antrenman Seansı</Label>
                      <p className="text-xs text-gray-400">İdmanlardan sonra otomatik antrenman seansı oluştur</p>
                    </div>
                    <Switch
                      checked={teamSettingsData.autoCreateSessions}
                      onCheckedChange={(checked) => setTeamSettingsData({ ...teamSettingsData, autoCreateSessions: checked })}
                    />
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button
                      type="submit"
                      disabled={savingSettings || (!logoFile && (!teamSettingsData.name.trim() || teamSettingsData.name === teamInfo?.name) && teamSettingsData.autoCreateSessions === teamInfo?.auto_create_sessions)}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      {savingSettings ? (t('common.loading') || 'Kaydediliyor...') : (t('common.save') || 'Kaydet')}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Recurring Training Modal */}
        <RecurringEventModal
          open={showRecurringModal}
          onOpenChange={setShowRecurringModal}
          onSubmit={handleSubmitRecurring}
        />

        {/* Premium Promotion Modal */}
        <PremiumPromotionModal
          open={showPremiumModal}
          onOpenChange={setShowPremiumModal}
        />
      </div >
    </TeamGate >
  );
};

export default Analytics;
